import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const GoalSettingPanel = ({ clientId, data, onChange, onComplete }) => {
  const [formData, setFormData] = useState({
    primaryGoal: '',
    specificGoals: [],
    timeline: '',
    targetWeight: '',
    targetBodyFat: '',
    performanceGoals: '',
    motivationFactors: [],
    barriers: '',
    support: '',
    checkInFrequency: '',
    successMetrics: [],
    rewardSystem: '',
    ...data
  });

  const [newGoal, setNewGoal] = useState({
    goal: '',
    specific: '',
    measurable: '',
    achievable: '',
    relevant: '',
    timeBound: ''
  });

  useEffect(() => {
    onChange(formData);
    checkCompletion();
  }, [formData]);

  const checkCompletion = () => {
    const required = ['primaryGoal', 'timeline', 'checkInFrequency'];
    const hasGoals = formData?.specificGoals?.length > 0;
    const isComplete = required?.every(field => formData?.[field]?.trim()) && hasGoals;
    onComplete(isComplete);
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNewGoalChange = (e) => {
    const { name, value } = e?.target;
    setNewGoal(prev => ({ ...prev, [name]: value }));
  };

  const addGoal = () => {
    if (newGoal?.goal?.trim()) {
      setFormData(prev => ({
        ...prev,
        specificGoals: [...prev?.specificGoals, { ...newGoal, id: Date.now() }]
      }));
      setNewGoal({
        goal: '',
        specific: '',
        measurable: '',
        achievable: '',
        relevant: '',
        timeBound: ''
      });
    }
  };

  const removeGoal = (goalId) => {
    setFormData(prev => ({
      ...prev,
      specificGoals: prev?.specificGoals?.filter(g => g?.id !== goalId)
    }));
  };

  const handleMotivationToggle = (factor) => {
    setFormData(prev => {
      const current = prev?.motivationFactors || [];
      const updated = current?.includes(factor)
        ? current?.filter(f => f !== factor)
        : [...current, factor];
      return { ...prev, motivationFactors: updated };
    });
  };

  const handleMetricToggle = (metric) => {
    setFormData(prev => {
      const current = prev?.successMetrics || [];
      const updated = current?.includes(metric)
        ? current?.filter(m => m !== metric)
        : [...current, metric];
      return { ...prev, successMetrics: updated };
    });
  };

  const primaryGoalOptions = [
    { value: 'weight-loss', label: 'Weight Loss', icon: 'TrendingDown' },
    { value: 'muscle-gain', label: 'Muscle Gain', icon: 'Dumbbell' },
    { value: 'strength', label: 'Increase Strength', icon: 'Zap' },
    { value: 'endurance', label: 'Build Endurance', icon: 'Heart' },
    { value: 'flexibility', label: 'Improve Flexibility', icon: 'Wind' },
    { value: 'health', label: 'General Health', icon: 'Activity' }
  ];

  const motivationFactors = [
    { value: 'health', label: 'Health Improvement', icon: 'Heart' },
    { value: 'appearance', label: 'Physical Appearance', icon: 'Eye' },
    { value: 'performance', label: 'Athletic Performance', icon: 'Trophy' },
    { value: 'energy', label: 'Energy Levels', icon: 'Zap' },
    { value: 'confidence', label: 'Self-Confidence', icon: 'Smile' },
    { value: 'social', label: 'Social Activities', icon: 'Users' }
  ];

  const successMetrics = [
    { value: 'weight', label: 'Weight Changes' },
    { value: 'measurements', label: 'Body Measurements' },
    { value: 'strength', label: 'Strength Gains' },
    { value: 'endurance', label: 'Endurance Improvements' },
    { value: 'energy', label: 'Energy Levels' },
    { value: 'consistency', label: 'Workout Consistency' },
    { value: 'photos', label: 'Progress Photos' },
    { value: 'performance', label: 'Performance Metrics' }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Goal Setting</h2>
        <p className="text-muted-foreground">Define SMART goals and success metrics for your fitness journey</p>
      </div>

      {/* Primary Goal Selection */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Target" size={20} className="text-primary" />
          Primary Fitness Goal
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {primaryGoalOptions?.map((option) => (
            <button
              key={option?.value}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, primaryGoal: option?.value }))}
              className={`
                p-4 rounded-xl border-2 transition-all hover:scale-105
                ${
                  formData?.primaryGoal === option?.value
                    ? 'border-primary bg-primary/10 shadow-lg'
                    : 'border-border hover:border-primary/50 bg-background'
                }
              `}
            >
              <div className="flex flex-col items-center gap-2">
                <div className={`
                  w-12 h-12 rounded-lg flex items-center justify-center
                  ${formData?.primaryGoal === option?.value ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}
                `}>
                  <Icon name={option?.icon} size={24} />
                </div>
                <span className="text-sm font-medium text-foreground text-center">{option?.label}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* SMART Goals */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="CheckSquare" size={20} className="text-primary" />
          SMART Goals Framework
        </h3>
        <p className="text-sm text-muted-foreground">Create specific, measurable, achievable, relevant, and time-bound goals</p>
        
        {/* Add New Goal Form */}
        <div className="bg-background rounded-lg border border-border p-4 space-y-3">
          <Input
            label="Goal Statement"
            name="goal"
            value={newGoal?.goal}
            onChange={handleNewGoalChange}
            placeholder="e.g., Lose 10kg and improve overall fitness"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Specific"
              name="specific"
              value={newGoal?.specific}
              onChange={handleNewGoalChange}
              placeholder="What exactly will you accomplish?"
              description="Be precise about what you want to achieve"
            />
            <Input
              label="Measurable"
              name="measurable"
              value={newGoal?.measurable}
              onChange={handleNewGoalChange}
              placeholder="How will you measure progress?"
              description="Define metrics to track"
            />
            <Input
              label="Achievable"
              name="achievable"
              value={newGoal?.achievable}
              onChange={handleNewGoalChange}
              placeholder="Is this realistic?"
              description="Consider your current fitness level"
            />
            <Input
              label="Relevant"
              name="relevant"
              value={newGoal?.relevant}
              onChange={handleNewGoalChange}
              placeholder="Why is this important?"
              description="Connect to your overall objectives"
            />
            <Input
              label="Time-Bound"
              name="timeBound"
              value={newGoal?.timeBound}
              onChange={handleNewGoalChange}
              placeholder="Target completion date?"
              description="Set a realistic deadline"
            />
          </div>
          <Button
            onClick={addGoal}
            disabled={!newGoal?.goal?.trim()}
            iconName="Plus"
            iconPosition="left"
            fullWidth
          >
            Add Goal
          </Button>
        </div>

        {/* Display Added Goals */}
        {formData?.specificGoals?.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium text-foreground">Your Goals ({formData?.specificGoals?.length})</h4>
            {formData?.specificGoals?.map((goal) => (
              <div key={goal?.id} className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-start justify-between mb-2">
                  <h5 className="font-semibold text-foreground">{goal?.goal}</h5>
                  <button
                    onClick={() => removeGoal(goal?.id)}
                    className="text-destructive hover:text-destructive/80"
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  {goal?.specific && <p><span className="font-medium text-muted-foreground">Specific:</span> {goal?.specific}</p>}
                  {goal?.measurable && <p><span className="font-medium text-muted-foreground">Measurable:</span> {goal?.measurable}</p>}
                  {goal?.achievable && <p><span className="font-medium text-muted-foreground">Achievable:</span> {goal?.achievable}</p>}
                  {goal?.relevant && <p><span className="font-medium text-muted-foreground">Relevant:</span> {goal?.relevant}</p>}
                  {goal?.timeBound && <p><span className="font-medium text-muted-foreground">Time-Bound:</span> {goal?.timeBound}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Timeline & Targets */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Calendar" size={20} className="text-primary" />
          Timeline & Targets
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Goal Timeline
            </label>
            <select
              name="timeline"
              value={formData?.timeline}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
            >
              <option value="">Select timeline...</option>
              <option value="1-month">1 Month</option>
              <option value="3-months">3 Months</option>
              <option value="6-months">6 Months</option>
              <option value="12-months">12 Months</option>
              <option value="ongoing">Ongoing</option>
            </select>
          </div>
          <Input
            label="Target Weight (kg)"
            name="targetWeight"
            type="number"
            step="0.1"
            value={formData?.targetWeight}
            onChange={handleInputChange}
            placeholder="65.0"
            description="If applicable"
          />
          <Input
            label="Target Body Fat (%)"
            name="targetBodyFat"
            type="number"
            step="0.1"
            value={formData?.targetBodyFat}
            onChange={handleInputChange}
            placeholder="18.0"
            description="If applicable"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Performance Goals
          </label>
          <textarea
            name="performanceGoals"
            value={formData?.performanceGoals}
            onChange={handleInputChange}
            className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
            rows={3}
            placeholder="e.g., Run 5km in under 25 minutes, bench press 80kg, complete 50 push-ups..."
          />
        </div>
      </div>

      {/* Motivation Factors */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Sparkles" size={20} className="text-primary" />
          Motivation Factors
        </h3>
        <p className="text-sm text-muted-foreground">What drives you to achieve your goals?</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {motivationFactors?.map((factor) => (
            <button
              key={factor?.value}
              type="button"
              onClick={() => handleMotivationToggle(factor?.value)}
              className={`
                p-3 rounded-lg border-2 transition-all hover:scale-[1.02]
                ${
                  formData?.motivationFactors?.includes(factor?.value)
                    ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50 bg-background'
                }
              `}
            >
              <div className="flex items-center gap-2">
                <div className={`
                  w-8 h-8 rounded-lg flex items-center justify-center
                  ${formData?.motivationFactors?.includes(factor?.value) ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}
                `}>
                  <Icon name={factor?.icon} size={16} />
                </div>
                <span className="text-sm font-medium text-foreground">{factor?.label}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Barriers & Support */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Shield" size={20} className="text-primary" />
          Barriers & Support System
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Potential Barriers
            </label>
            <textarea
              name="barriers"
              value={formData?.barriers}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
              rows={4}
              placeholder="What obstacles might prevent you from reaching your goals? (time, motivation, injuries, etc.)"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Support System
            </label>
            <textarea
              name="support"
              value={formData?.support}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
              rows={4}
              placeholder="Who or what will support you? (family, friends, workout partners, resources, etc.)"
            />
          </div>
        </div>
      </div>

      {/* Success Metrics */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="TrendingUp" size={20} className="text-primary" />
          Success Metrics
        </h3>
        <p className="text-sm text-muted-foreground">How will you measure success?</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {successMetrics?.map((metric) => (
            <button
              key={metric?.value}
              type="button"
              onClick={() => handleMetricToggle(metric?.value)}
              className={`
                p-3 rounded-lg border-2 transition-all text-left hover:scale-[1.02]
                ${
                  formData?.successMetrics?.includes(metric?.value)
                    ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50 bg-background'
                }
              `}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">{metric?.label}</span>
                {formData?.successMetrics?.includes(metric?.value) && (
                  <Icon name="Check" size={16} className="text-primary" />
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Check-in & Accountability */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Clock" size={20} className="text-primary" />
          Check-in & Accountability
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Check-in Frequency
            </label>
            <select
              name="checkInFrequency"
              value={formData?.checkInFrequency}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
            >
              <option value="">Select frequency...</option>
              <option value="weekly">Weekly</option>
              <option value="bi-weekly">Bi-weekly</option>
              <option value="monthly">Monthly</option>
              <option value="as-needed">As Needed</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Reward System
            </label>
            <input
              type="text"
              name="rewardSystem"
              value={formData?.rewardSystem}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
              placeholder="How will you celebrate milestones?"
            />
          </div>
        </div>
      </div>

      {/* Summary Card */}
      <div className="bg-primary/10 border border-primary rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Lightbulb" size={20} className="text-primary mt-1" />
          <div>
            <h4 className="font-semibold text-foreground mb-2">Goal Setting Tips</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Break large goals into smaller, manageable milestones</li>
              <li>• Review and adjust goals regularly based on progress</li>
              <li>• Celebrate small wins along the way</li>
              <li>• Be flexible and adapt to challenges</li>
              <li>• Focus on progress, not perfection</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoalSettingPanel;